import React, { FormEvent, useEffect, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Bug,
  Clock3,
  Handshake,
  Lightbulb,
  Mail,
  MessageSquareText,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { APIError } from '../services/api';
import {
  ContactCategory,
  ContactMessage,
  contactService,
} from '../services/contactService';
import './Contact.css';

const categories: Array<{
  value: ContactCategory;
  label: string;
  helper: string;
  icon: typeof Bug;
}> = [
  { value: 'technical', label: 'Technical issue', helper: 'Something is not working', icon: Bug },
  { value: 'content', label: 'Content correction', helper: 'Report an inaccurate lesson', icon: BookOpenCheck },
  { value: 'account', label: 'Account help', helper: 'Login, profile, or progress', icon: UserRound },
  { value: 'feedback', label: 'Feedback', helper: 'Share an improvement idea', icon: Lightbulb },
  { value: 'partnership', label: 'Partnership', helper: 'Business or educator enquiry', icon: Handshake },
  { value: 'other', label: 'Something else', helper: 'Any other question', icon: MessageSquareText },
];

const initialForm: ContactMessage = {
  name: '',
  email: '',
  category: 'technical',
  subject: '',
  message: '',
  pageUrl: '',
  website: '',
};

const Contact: React.FC = () => {
  const { state } = useAuth();
  const [form, setForm] = useState<ContactMessage>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [referenceId, setReferenceId] = useState('');

  useEffect(() => {
    if (!state.user) return;
    setForm((current) => ({
      ...current,
      name: current.name || state.user?.fullName || '',
      email: current.email || state.user?.email || '',
    }));
  }, [state.user]);

  const updateField = (field: keyof ContactMessage, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (error) setError('');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await contactService.send({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        pageUrl: form.pageUrl?.trim() || undefined,
      });
      setReferenceId(response.referenceId);
    } catch (submitError) {
      setError(
        submitError instanceof APIError
          ? submitError.message
          : 'We could not send your message. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setReferenceId('');
    setError('');
    setForm({
      ...initialForm,
      name: state.user?.fullName || '',
      email: state.user?.email || '',
    });
  };

  return (
    <div className="contact-page">
      <div className="contact-page__glow" aria-hidden="true" />
      <section className="contact-shell">
        <header className="contact-hero">
          <span className="contact-eyebrow"><Mail size={14} /> Contact LetsBoostUp</span>
          <h1>How can we help?</h1>
          <p>
            Found a content gap, hit a technical issue, or have an idea worth discussing?
            Send the details directly to the team.
          </p>
        </header>

        <div className="contact-layout">
          <aside className="contact-aside" aria-label="Contact guidance">
            <div className="contact-aside__intro">
              <span className="contact-aside__icon"><MessageSquareText size={20} /></span>
              <div>
                <h2>Send useful context</h2>
                <p>A few precise details help us investigate and respond faster.</p>
              </div>
            </div>

            <div className="contact-guidance">
              <div><span>01</span><p>Mention what you expected to happen.</p></div>
              <div><span>02</span><p>Include the affected page URL when relevant.</p></div>
              <div><span>03</span><p>Never include passwords or private credentials.</p></div>
            </div>

            <div className="contact-response-note">
              <Clock3 size={17} />
              <div>
                <strong>Typical response</strong>
                <span>Within 1–2 working days</span>
              </div>
            </div>
          </aside>

          <div className="contact-form-card">
            {referenceId ? (
              <div className="contact-success" role="status" aria-live="polite">
                <span className="contact-success__icon"><BadgeCheck size={32} /></span>
                <p className="contact-success__eyebrow">Message received</p>
                <h2>Thanks for reaching out.</h2>
                <p>Your message is now with the LetsBoostUp team. You can reply directly when we email you back.</p>
                <div className="contact-success__reference">
                  <span>Reference</span>
                  <code>{referenceId}</code>
                </div>
                <button type="button" className="contact-secondary-button" onClick={reset}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="contact-form">
                <div className="contact-form__heading">
                  <div>
                    <p>Tell us what you need</p>
                    <h2>Start a conversation</h2>
                  </div>
                  <span>All fields marked * are required</span>
                </div>

                <fieldset className="contact-category-fieldset">
                  <legend>What is this about? *</legend>
                  <div className="contact-category-grid">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <label key={category.value} className={form.category === category.value ? 'is-selected' : ''}>
                          <input
                            type="radio"
                            name="category"
                            value={category.value}
                            checked={form.category === category.value}
                            onChange={() => updateField('category', category.value)}
                          />
                          <Icon size={17} />
                          <span><strong>{category.label}</strong><small>{category.helper}</small></span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <div className="contact-field-row">
                  <label className="contact-field">
                    <span>Name *</span>
                    <input
                      required
                      minLength={2}
                      maxLength={80}
                      autoComplete="name"
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                      placeholder="Your name"
                    />
                  </label>
                  <label className="contact-field">
                    <span>Email *</span>
                    <input
                      required
                      type="email"
                      maxLength={160}
                      autoComplete="email"
                      value={form.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      placeholder="you@example.com"
                    />
                  </label>
                </div>

                <label className="contact-field">
                  <span>Subject *</span>
                  <input
                    required
                    minLength={3}
                    maxLength={120}
                    value={form.subject}
                    onChange={(event) => updateField('subject', event.target.value)}
                    placeholder="A short summary"
                  />
                </label>

                <label className="contact-field">
                  <span>Page URL <small>Optional</small></span>
                  <input
                    type="url"
                    maxLength={500}
                    value={form.pageUrl}
                    onChange={(event) => updateField('pageUrl', event.target.value)}
                    placeholder="https://letsboostup.com/..."
                  />
                  <small>Useful when reporting a lesson, quiz, or playground issue.</small>
                </label>

                <label className="contact-field">
                  <span>Message *</span>
                  <textarea
                    required
                    minLength={20}
                    maxLength={5000}
                    rows={7}
                    value={form.message}
                    onChange={(event) => updateField('message', event.target.value)}
                    placeholder="Describe the issue, suggestion, or enquiry with enough context for us to act on it."
                  />
                  <small className="contact-character-count">{form.message.length} / 5000</small>
                </label>

                <label className="contact-honeypot" aria-hidden="true">
                  Website
                  <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(event) => updateField('website', event.target.value)}
                  />
                </label>

                {error && <div className="contact-error" role="alert">{error}</div>}

                <div className="contact-form__footer">
                  <p>By sending this form, you agree that we may reply to the email provided.</p>
                  <button type="submit" className="contact-submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending…' : 'Send message'}
                    {!isSubmitting && <ArrowRight size={17} />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
